/**
 * Activity Log Page
 * Comprehensive audit log with filtering, search, and analytics
 */

import { useCallback, useEffect, useState } from "react"
import { apiClient } from "../lib/api"
import { formatDate } from "../lib/utils"
import type { ActivityLogEntry } from "../types"

interface Filters {
  action?: string
  resourceType?: string
  adminUser?: string
  dateFrom?: string
  dateTo?: string
  search?: string
}

const ACTION_COLORS: Record<string, string> = {
  create_org: "success",
  update_org: "warning",
  delete_org: "danger",
  create_user: "success",
  update_user: "warning",
  delete_user: "danger",
  create_api_key: "success",
  revoke_api_key: "danger",
  update_api_key: "warning",
  adjust_balance: "warning",
  login: "info",
  logout: "default",
}

const ACTION_ICONS: Record<string, string> = {
  create_org: "➕",
  update_org: "✏️",
  delete_org: "🗑️",
  create_user: "👤",
  update_user: "✏️",
  delete_user: "❌",
  create_api_key: "🔑",
  revoke_api_key: "🚫",
  update_api_key: "✏️",
  adjust_balance: "💰",
  login: "🔓",
  logout: "🔒",
}

export function ActivityLogPage() {
  const [logs, setLogs] = useState<ActivityLogEntry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [filters, setFilters] = useState<Filters>({})
  const [expandedLog, setExpandedLog] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [showAnalytics, setShowAnalytics] = useState(false)

  // Analytics state
  const [analytics, setAnalytics] = useState({
    totalActions: 0,
    actionsByType: {} as Record<string, number>,
    actionsByAdmin: {} as Record<string, number>,
    actionsByDay: {} as Record<string, number>,
  })

  const loadActivityLog = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await apiClient.getActivityLog({ page, limit: 50 })
      setLogs(response.data)
      setHasMore(response.data.length === 50)
    } catch (err) {
      console.error("Failed to load activity log:", err)
    } finally {
      setIsLoading(false)
    }
  }, [page])

  useEffect(() => {
    loadActivityLog()
  }, [loadActivityLog])

  const calculateAnalytics = useCallback(() => {
    const actionsByType: Record<string, number> = {}
    const actionsByAdmin: Record<string, number> = {}
    const actionsByDay: Record<string, number> = {}

    for (const log of logs) {
      // Count by action type
      actionsByType[log.action] = (actionsByType[log.action] || 0) + 1

      // Count by admin user
      const adminKey = log.adminUsername || "Unknown"
      actionsByAdmin[adminKey] = (actionsByAdmin[adminKey] || 0) + 1

      // Count by day
      const day = new Date(log.createdAt).toLocaleDateString()
      actionsByDay[day] = (actionsByDay[day] || 0) + 1
    }

    setAnalytics({
      totalActions: logs.length,
      actionsByType,
      actionsByAdmin,
      actionsByDay,
    })
  }, [logs])

  useEffect(() => {
    if (logs.length > 0) {
      calculateAnalytics()
    }
  }, [logs, calculateAnalytics])

  const getFilteredLogs = () => {
    let filtered = [...logs]

    if (filters.action) {
      filtered = filtered.filter((log) => log.action === filters.action)
    }

    if (filters.resourceType) {
      filtered = filtered.filter((log) => log.resourceType === filters.resourceType)
    }

    if (filters.adminUser) {
      filtered = filtered.filter((log) => log.adminUsername === filters.adminUser)
    }

    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom)
      filtered = filtered.filter((log) => new Date(log.createdAt) >= fromDate)
    }

    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo)
      toDate.setHours(23, 59, 59, 999)
      filtered = filtered.filter((log) => new Date(log.createdAt) <= toDate)
    }

    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      filtered = filtered.filter(
        (log) =>
          log.action.toLowerCase().includes(searchLower) ||
          log.resourceType.toLowerCase().includes(searchLower) ||
          log.resourceId?.toLowerCase().includes(searchLower) ||
          log.adminUsername.toLowerCase().includes(searchLower) ||
          log.adminEmail.toLowerCase().includes(searchLower) ||
          JSON.stringify(log.details).toLowerCase().includes(searchLower),
      )
    }

    return filtered
  }

  const exportToCSV = () => {
    const filtered = getFilteredLogs()
    const headers = ["Timestamp", "Admin User", "Action", "Resource Type", "Resource ID", "Details", "IP Address"]
    const rows = filtered.map((log) => [
      new Date(log.createdAt).toISOString(),
      log.adminUsername || "Unknown",
      log.action,
      log.resourceType,
      log.resourceId || "",
      JSON.stringify(log.details || {}),
      log.ipAddress || "",
    ])

    const csv = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(",")).join("\n")

    const blob = new Blob([csv], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `activity-log-${new Date().toISOString().split("T")[0]}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const clearFilters = () => {
    setFilters({})
  }

  const filteredLogs = getFilteredLogs()
  const uniqueActions = Array.from(new Set(logs.map((log) => log.action)))
  const uniqueResourceTypes = Array.from(new Set(logs.map((log) => log.resourceType)))
  const uniqueAdmins = Array.from(new Set(logs.map((log) => log.adminUsername).filter(Boolean)))

  const getRelativeTime = (date: string) => {
    const now = new Date()
    const past = new Date(date)
    const diffMs = now.getTime() - past.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return "Just now"
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return formatDate(date)
  }

  const formatActionName = (action: string) => {
    return action
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  }

  if (isLoading && logs.length === 0) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <p style={{ color: "#94a3b8", fontSize: "0.9375rem" }}>Loading activity log...</p>
      </div>
    )
  }

  return (
    <div style={{ padding: "2rem" }}>
      {/* Header */}
      <div style={{ marginBottom: "2rem" }}>
        <h1 style={{ fontSize: "1.875rem", fontWeight: 700, color: "#f1f5f9", marginBottom: "0.5rem" }}>
          Activity Log
        </h1>
        <p style={{ color: "#94a3b8", fontSize: "0.9375rem" }}>Comprehensive audit trail of all admin actions</p>
      </div>

      {/* Actions Bar */}
      <div style={{ display: "flex", gap: "1rem", marginBottom: "2rem", flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={() => setShowAnalytics(!showAnalytics)}
          style={{
            padding: "0.5rem 1rem",
            background: showAnalytics
              ? "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)"
              : "rgba(255, 255, 255, 0.05)",
            border: `1px solid ${showAnalytics ? "#6366f1" : "rgba(255, 255, 255, 0.1)"}`,
            borderRadius: "8px",
            color: "white",
            fontSize: "0.875rem",
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          📊 {showAnalytics ? "Hide" : "Show"} Analytics
        </button>
        <button
          type="button"
          onClick={exportToCSV}
          style={{
            padding: "0.5rem 1rem",
            background: "rgba(255, 255, 255, 0.05)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            borderRadius: "8px",
            color: "white",
            fontSize: "0.875rem",
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          📥 Export CSV
        </button>
        <button
          type="button"
          onClick={loadActivityLog}
          style={{
            padding: "0.5rem 1rem",
            background: "rgba(255, 255, 255, 0.05)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            borderRadius: "8px",
            color: "white",
            fontSize: "0.875rem",
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          🔄 Refresh
        </button>
      </div>

      {/* Analytics Section */}
      {showAnalytics && (
        <div style={{ marginBottom: "2rem" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "1rem",
              marginBottom: "1.5rem",
            }}
          >
            <div className="card" style={{ padding: "1.5rem" }}>
              <p style={{ color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}>Total Actions</p>
              <p style={{ fontSize: "2rem", fontWeight: 700, color: "#f1f5f9" }}>{analytics.totalActions}</p>
            </div>
            <div className="card" style={{ padding: "1.5rem" }}>
              <p style={{ color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}>Most Common Action</p>
              <p style={{ fontSize: "1.25rem", fontWeight: 600, color: "#f1f5f9" }}>
                {Object.entries(analytics.actionsByType)
                  .sort((a, b) => b[1] - a[1])[0]?.[0]
                  ?.split("_")
                  .join(" ") || "N/A"}
              </p>
            </div>
            <div className="card" style={{ padding: "1.5rem" }}>
              <p style={{ color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}>Most Active Admin</p>
              <p style={{ fontSize: "1.25rem", fontWeight: 600, color: "#f1f5f9" }}>
                {Object.entries(analytics.actionsByAdmin).sort((a, b) => b[1] - a[1])[0]?.[0] || "N/A"}
              </p>
            </div>
          </div>

          {/* Action Type Distribution */}
          <div className="card" style={{ padding: "1.5rem", marginBottom: "1rem" }}>
            <h3 style={{ fontSize: "1.125rem", fontWeight: 600, color: "#e2e8f0", marginBottom: "1rem" }}>
              Actions by Type
            </h3>
            <div
              style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "0.75rem" }}
            >
              {Object.entries(analytics.actionsByType)
                .sort((a, b) => b[1] - a[1])
                .map(([action, count]) => (
                  <div
                    key={action}
                    style={{
                      padding: "0.75rem",
                      background: "rgba(255, 255, 255, 0.05)",
                      borderRadius: "6px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span style={{ color: "#e2e8f0", fontSize: "0.875rem" }}>
                      {ACTION_ICONS[action]} {formatActionName(action)}
                    </span>
                    <span
                      style={{
                        color: "#94a3b8",
                        fontSize: "0.875rem",
                        fontWeight: 600,
                      }}
                    >
                      {count}
                    </span>
                  </div>
                ))}
            </div>
          </div>

          {/* Activity Heatmap */}
          <div className="card" style={{ padding: "1.5rem" }}>
            <h3 style={{ fontSize: "1.125rem", fontWeight: 600, color: "#e2e8f0", marginBottom: "1rem" }}>
              Activity Over Time
            </h3>
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
              {Object.entries(analytics.actionsByDay)
                .sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime())
                .map(([day, count]) => {
                  const maxCount = Math.max(...Object.values(analytics.actionsByDay))
                  const intensity = count / maxCount
                  return (
                    <div
                      key={day}
                      style={{
                        padding: "0.75rem",
                        background: `rgba(99, 102, 241, ${0.2 + intensity * 0.6})`,
                        borderRadius: "6px",
                        minWidth: "120px",
                      }}
                    >
                      <div style={{ color: "#e2e8f0", fontSize: "0.75rem", marginBottom: "0.25rem" }}>{day}</div>
                      <div style={{ color: "#f1f5f9", fontSize: "1.125rem", fontWeight: 600 }}>{count}</div>
                    </div>
                  )
                })}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card" style={{ padding: "1.5rem", marginBottom: "1.5rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <h3 style={{ fontSize: "1.125rem", fontWeight: 600, color: "#e2e8f0" }}>Filters</h3>
          {Object.keys(filters).some((key) => filters[key as keyof Filters]) && (
            <button
              type="button"
              onClick={clearFilters}
              style={{
                padding: "0.25rem 0.75rem",
                background: "rgba(239, 68, 68, 0.1)",
                border: "1px solid rgba(239, 68, 68, 0.3)",
                borderRadius: "6px",
                color: "#fca5a5",
                fontSize: "0.8125rem",
                cursor: "pointer",
              }}
            >
              Clear All
            </button>
          )}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem" }}>
          <div>
            <label htmlFor="search-input" style={{ display: "block", color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}>
              Search
            </label>
            <input
              id="search-input"
              type="text"
              placeholder="Search anything..."
              value={filters.search || ""}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              style={{
                width: "100%",
                padding: "0.5rem",
                background: "rgba(255, 255, 255, 0.05)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                borderRadius: "6px",
                color: "#e2e8f0",
              }}
            />
          </div>
          <div>
            <label htmlFor="action-filter" style={{ display: "block", color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}>
              Action Type
            </label>
            <select
              id="action-filter"
              value={filters.action || ""}
              onChange={(e) => setFilters({ ...filters, action: e.target.value || undefined })}
              style={{
                width: "100%",
                padding: "0.5rem",
                background: "rgba(255, 255, 255, 0.05)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                borderRadius: "6px",
                color: "#e2e8f0",
              }}
            >
              <option value="">All Actions</option>
              {uniqueActions.map((action) => (
                <option key={action} value={action}>
                  {formatActionName(action)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="resource-filter" style={{ display: "block", color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}>
              Resource Type
            </label>
            <select
              id="resource-filter"
              value={filters.resourceType || ""}
              onChange={(e) => setFilters({ ...filters, resourceType: e.target.value || undefined })}
              style={{
                width: "100%",
                padding: "0.5rem",
                background: "rgba(255, 255, 255, 0.05)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                borderRadius: "6px",
                color: "#e2e8f0",
              }}
            >
              <option value="">All Resources</option>
              {uniqueResourceTypes.map((type) => (
                <option key={type} value={type}>
                  {type
                    .split("_")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="admin-filter" style={{ display: "block", color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}>
              Admin User
            </label>
            <select
              id="admin-filter"
              value={filters.adminUser || ""}
              onChange={(e) => setFilters({ ...filters, adminUser: e.target.value || undefined })}
              style={{
                width: "100%",
                padding: "0.5rem",
                background: "rgba(255, 255, 255, 0.05)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                borderRadius: "6px",
                color: "#e2e8f0",
              }}
            >
              <option value="">All Admins</option>
              {uniqueAdmins.map((admin) => (
                <option key={admin} value={admin}>
                  {admin}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="date-from" style={{ display: "block", color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}>
              Date From
            </label>
            <input
              id="date-from"
              type="date"
              value={filters.dateFrom || ""}
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value || undefined })}
              style={{
                width: "100%",
                padding: "0.5rem",
                background: "rgba(255, 255, 255, 0.05)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                borderRadius: "6px",
                color: "#e2e8f0",
              }}
            />
          </div>
          <div>
            <label htmlFor="date-to" style={{ display: "block", color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem" }}>
              Date To
            </label>
            <input
              id="date-to"
              type="date"
              value={filters.dateTo || ""}
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value || undefined })}
              style={{
                width: "100%",
                padding: "0.5rem",
                background: "rgba(255, 255, 255, 0.05)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                borderRadius: "6px",
                color: "#e2e8f0",
              }}
            />
          </div>
        </div>
        <div style={{ marginTop: "1rem", color: "#94a3b8", fontSize: "0.875rem" }}>
          Showing {filteredLogs.length} of {logs.length} activities
        </div>
      </div>

      {/* Activity Timeline */}
      <div className="card">
        <div style={{ padding: "1.5rem", borderBottom: "1px solid rgba(255, 255, 255, 0.05)" }}>
          <h3 style={{ fontSize: "1.25rem", fontWeight: 600, color: "#e2e8f0" }}>Activity Timeline</h3>
        </div>
        <div style={{ padding: "1rem" }}>
          {filteredLogs.length === 0 ? (
            <div style={{ padding: "3rem", textAlign: "center" }}>
              <p style={{ color: "#64748b", fontSize: "0.9375rem" }}>No activities match your filters</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {filteredLogs.map((log, index) => (
                <div key={log.id}>
                  <button
                    type="button"
                    onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      padding: "1rem",
                      background: expandedLog === log.id ? "rgba(99, 102, 241, 0.1)" : "rgba(255, 255, 255, 0.03)",
                      border: "none",
                      borderLeft: `4px solid ${
                        ACTION_COLORS[log.action] === "success"
                          ? "#22c55e"
                          : ACTION_COLORS[log.action] === "warning"
                            ? "#f59e0b"
                            : ACTION_COLORS[log.action] === "danger"
                              ? "#ef4444"
                              : ACTION_COLORS[log.action] === "info"
                                ? "#3b82f6"
                                : "#64748b"
                      }`,
                      borderRadius: "6px",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      font: "inherit",
                    }}
                    onMouseEnter={(e) => {
                      if (expandedLog !== log.id) {
                        e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)"
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (expandedLog !== log.id) {
                        e.currentTarget.style.background = "rgba(255, 255, 255, 0.03)"
                      }
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: "0.5rem",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flex: 1 }}>
                        <span style={{ fontSize: "1.25rem" }}>{ACTION_ICONS[log.action]}</span>
                        <div>
                          <div
                            style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.25rem" }}
                          >
                            <span style={{ color: "#e2e8f0", fontWeight: 600, fontSize: "0.9375rem" }}>
                              {formatActionName(log.action)}
                            </span>
                            <span
                              className={`badge badge-${ACTION_COLORS[log.action]}`}
                              style={{ fontSize: "0.75rem" }}
                            >
                              {log.resourceType.replace("_", " ")}
                            </span>
                          </div>
                          <div style={{ color: "#94a3b8", fontSize: "0.8125rem" }}>
                            by <strong>{log.adminUsername || "Unknown"}</strong>
                            {log.adminEmail && ` (${log.adminEmail})`}
                          </div>
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ color: "#94a3b8", fontSize: "0.8125rem" }}>{getRelativeTime(log.createdAt)}</div>
                        <div style={{ color: "#64748b", fontSize: "0.75rem" }}>
                          {new Date(log.createdAt).toLocaleTimeString()}
                        </div>
                      </div>
                    </div>

                    {log.resourceId && (
                      <div style={{ color: "#94a3b8", fontSize: "0.8125rem", marginBottom: "0.5rem" }}>
                        Resource ID:{" "}
                        <code
                          style={{
                            color: "#e2e8f0",
                            background: "rgba(0, 0, 0, 0.3)",
                            padding: "0.125rem 0.375rem",
                            borderRadius: "3px",
                          }}
                        >
                          {log.resourceId}
                        </code>
                      </div>
                    )}

                    {log.ipAddress && <div style={{ color: "#64748b", fontSize: "0.75rem" }}>IP: {log.ipAddress}</div>}

                    {/* Expanded Details */}
                    {expandedLog === log.id && log.details && (
                      <div
                        style={{
                          marginTop: "1rem",
                          padding: "1rem",
                          background: "rgba(0, 0, 0, 0.3)",
                          borderRadius: "6px",
                        }}
                      >
                        <div
                          style={{ color: "#94a3b8", fontSize: "0.875rem", marginBottom: "0.5rem", fontWeight: 600 }}
                        >
                          Details:
                        </div>
                        <pre
                          style={{
                            color: "#e2e8f0",
                            fontSize: "0.8125rem",
                            fontFamily: "monospace",
                            overflow: "auto",
                            margin: 0,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {JSON.stringify(log.details, null, 2)}
                        </pre>
                      </div>
                    )}
                  </button>
                  {index < filteredLogs.length - 1 && (
                    <div
                      style={{
                        height: "1px",
                        background: "rgba(255, 255, 255, 0.05)",
                        margin: "0.5rem 0",
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Load More */}
        {hasMore && (
          <div style={{ padding: "1rem", borderTop: "1px solid rgba(255, 255, 255, 0.05)", textAlign: "center" }}>
            <button
              type="button"
              onClick={() => setPage(page + 1)}
              disabled={isLoading}
              style={{
                padding: "0.75rem 1.5rem",
                background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                border: "none",
                borderRadius: "8px",
                color: "white",
                fontSize: "0.875rem",
                fontWeight: 500,
                cursor: isLoading ? "not-allowed" : "pointer",
                opacity: isLoading ? 0.5 : 1,
              }}
            >
              {isLoading ? "Loading..." : "Load More"}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
